import { useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { features } from "@/lib/features";
import { numberRuns } from "@/lib/run-trend";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { useSetBreadcrumb } from "@/components/layout/breadcrumb-context";
import { Page } from "@/components/layout/page";
import {
  EntityNotFound,
  EntityErrorState,
} from "@/components/layout/entity-state";
import { isValidUuid } from "@/lib/utils";
import { ActionBar } from "@/components/layout/action-bar";
import { DangerZone } from "@/components/layout/danger-zone";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { Dateline } from "@/components/ui/dateline";
import { CanEdit, useCanEdit } from "@/lib/project-access";
import { EditPersonaDialog } from "@/components/persona/edit-persona-dialog";
import { PersonaIdentitySection } from "@/components/persona/persona-identity-section";
import { PersonaScenariosSection } from "@/components/persona/persona-scenarios-section";
import { PersonaWalkthroughsSection } from "@/components/persona/persona-walkthroughs-section";
import { PersonaFindingsSection } from "@/components/persona/persona-findings-section";

export function PersonaDetailPage() {
  const { id: projectId, personaId } = useParams<{
    id: string;
    personaId: string;
  }>();
  const isValidId = isValidUuid(personaId);
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const canEdit = useCanEdit();
  const [showEdit, setShowEdit] = useState(false);

  const {
    data: persona,
    isLoading,
    error,
    refetch,
  } = trpc.persona.getById.useQuery(
    { id: personaId ?? "" },
    { enabled: isValidId },
  );
  useSetBreadcrumb(persona?.name);

  const deleteMutation = trpc.persona.delete.useMutation({
    onSuccess: (result) => {
      toast.success(result.archived ? "Persona archived" : "Persona deleted");
      if (projectId) {
        utils.persona.list.invalidate({ projectId });
      }
      navigate(`/projects/${projectId}/personas`);
    },
    onError: (err) => toastTrpcError(err, "Failed to delete persona"),
  });

  const runNumbers = useMemo(() => {
    if (!persona?.walkthroughs) return new Map<string, number>();
    const byScenario = new Map<string, typeof persona.walkthroughs>();
    for (const wt of persona.walkthroughs) {
      const list = byScenario.get(wt.scenarioId) ?? [];
      list.push(wt);
      byScenario.set(wt.scenarioId, list);
    }
    const map = new Map<string, number>();
    for (const runs of byScenario.values()) {
      const numbered = numberRuns(
        runs.map((r) => ({
          id: r.id,
          completedAt: r.completedAt,
          createdAt: r.completedAt ?? new Date(0).toISOString(),
        })),
      );
      for (const [id, num] of numbered.entries()) {
        map.set(id, num);
      }
    }
    return map;
  }, [persona?.walkthroughs]);

  if (isLoading) {
    return (
      <Page
        title={<Skeleton className="h-8 w-64" />}
        back={{ to: `/projects/${projectId}/personas`, label: "Personas" }}
        maxWidth="max-w-[960px]"
      >
        <Skeleton className="h-64 w-full" />
      </Page>
    );
  }

  if (!isValidId || error?.data?.code === "NOT_FOUND" || !persona) {
    return (
      <EntityNotFound
        title="Persona not found"
        entityName="persona"
        backLink={{
          to: `/projects/${projectId ?? ""}/personas`,
          label: "Back to Personas",
        }}
      />
    );
  }

  if (error) {
    return (
      <EntityErrorState
        title="Failed to load persona"
        onRetry={() => refetch()}
        backLink={{
          to: `/projects/${projectId ?? ""}/personas`,
          label: "Back to Personas",
        }}
      />
    );
  }

  const firstName = persona.name.split(" ")[0];
  const completedWalkthroughCount = persona.walkthroughs.filter(
    (wt) => wt.status === "completed",
  ).length;

  const primaryAction = canEdit ? (
    <Button onClick={() => setShowEdit(true)}>
      <Pencil className="mr-1.5 size-3.5" />
      Edit persona
    </Button>
  ) : undefined;

  const secondaryActions =
    features.liveWalkthrough && canEdit
      ? [
          <Button key="live" variant="outline" asChild>
            <Link
              to={`/projects/${projectId}/live-walkthrough?personaId=${persona.id}`}
            >
              Live walkthrough →
            </Link>
          </Button>,
        ]
      : undefined;

  return (
    <Page
      title={persona.name}
      back={{ to: `/projects/${projectId}/personas`, label: "Personas" }}
      maxWidth="max-w-[960px]"
      actions={
        primaryAction || secondaryActions ? (
          <ActionBar primary={primaryAction} secondary={secondaryActions} />
        ) : undefined
      }
    >
      <div className="space-y-10">
        {/* ── Dateline ────────────────────────────────────────────── */}
        <Dateline
          items={[
            { label: "Role", value: persona.role },
            ...(persona.status === "archived"
              ? [{ label: "Status", value: "Archived" }]
              : []),
            {
              label: "Scenarios",
              value: `${persona.linkedScenarios.length} attached`,
            },
            {
              label: "Walkthroughs",
              value: `${completedWalkthroughCount} completed`,
            },
            {
              label: "Findings",
              value:
                persona.findings.length > 0 ? (
                  <Link
                    to={`/projects/${projectId}/findings?persona=${persona.id}`}
                    className="font-medium text-primary hover:underline"
                    title={`View ${persona.findings.length} findings for ${firstName} in triage queue`}
                  >
                    {persona.findings.length} recorded →
                  </Link>
                ) : (
                  "0 recorded"
                ),
            },
          ]}
        />

        {/* ── Identity + background ───────────────────────────────── */}
        <PersonaIdentitySection
          persona={persona}
          onEdit={() => setShowEdit(true)}
        />

        {/* ── Linked scenarios ────────────────────────────────────── */}
        <PersonaScenariosSection
          projectId={projectId!}
          personaId={persona.id}
          firstName={firstName}
          linkedScenarios={persona.linkedScenarios}
        />

        {/* ── Walkthroughs ────────────────────────────────────────── */}
        <PersonaWalkthroughsSection
          walkthroughs={persona.walkthroughs}
          projectId={projectId!}
          firstName={firstName}
          runNumbers={runNumbers}
        />

        {/* ── Findings ────────────────────────────────────────────── */}
        <PersonaFindingsSection
          findings={persona.findings}
          projectId={projectId!}
          personaId={persona.id}
          firstName={firstName}
        />
      </div>

      {/* ── Edit Dialog ─────────────────────────────────────────── */}
      <EditPersonaDialog
        open={showEdit}
        onOpenChange={setShowEdit}
        persona={persona}
      />

      <CanEdit>
        <DangerZone
          title={`Delete ${persona.name}`}
          description={`This persona will be deleted. If ${firstName} has been used in walkthroughs, the record will be archived instead so past findings stay intact.`}
          actionLabel="Delete persona"
          confirmTitle={`Delete ${persona.name}?`}
          confirmDescription={`${firstName} will be deleted. If this persona has been used in walkthroughs, the record will be archived instead.`}
          onConfirm={() => deleteMutation.mutate({ id: persona.id })}
          isPending={deleteMutation.isPending}
        />
      </CanEdit>
    </Page>
  );
}
